import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { fetchDomains, fetchExchangeRateBanxico } from '../api/domains.api';
import { calculateDomainCurrentPrice, safeDate } from '../utils/domainCalculator';


import WebIcon from '@mui/icons-material/Web';
import EmailIcon from '@mui/icons-material/Email';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CloudIcon from '@mui/icons-material/Cloud';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import SearchIcon from '@mui/icons-material/Search';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import WebAssetOffIcon from '@mui/icons-material/WebAssetOff';
import WebAssetIcon from '@mui/icons-material/WebAsset';
import styles from './DominioDetail.module.css';

export default function DominioDetail() {
  const { domainName } = useParams();
  const [domainInfo, setDomainInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState('MXN');
  const [exchangeRate, setExchangeRate] = useState(null);

  useEffect(() => {
    const loadDomain = async () => {
      try {
        const data = await fetchDomains();
        const domain = data.find((d) => d.domain === domainName);
        setDomainInfo(domain);
      } catch (err) {
        console.error('Error al cargar dominio:', err);
      } finally {
        setLoading(false);
      }
    };
    loadDomain();
  }, [domainName]);

  useEffect(() => {
    if (currency === 'USD' && !exchangeRate) {
      loadExchangeRate();
    }
  }, [currency, exchangeRate]);

  const loadExchangeRate = async () => {
    try {
      const data = await fetchExchangeRateBanxico();
      const rate = parseFloat(data.bmx.series[0].datos[0].dato);
      setExchangeRate(rate);
    } catch (error) {
      console.error('Error al obtener tipo de cambio de Banxico:', error);
    }
  };

  const calculateCurrentPrice = (domain) => {
    return calculateDomainCurrentPrice(domain);
  };


  const formatPrice = (price) => {
    if (currency === 'USD' && exchangeRate) {
      const usdPrice = price / exchangeRate;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(usdPrice);
    }
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(price);
  };

  if (loading) {
    return <div className={styles.errorContainer}>Cargando información...</div>;
  }

  if (!domainInfo) {
    return (
      <div className={styles.errorContainer}>
        <h2 className={styles.errorTitle}>Dominio no encontrado</h2>
        <p className={styles.errorDesc}>El dominio solicitado no está disponible en nuestro catálogo.</p>
        <Link to="/dominios" className={styles.actionButton} style={{ maxWidth: '200px' }}>
          Volver al catálogo
        </Link>
      </div>
    );
  }

  const currentPrice = calculateCurrentPrice(domainInfo);
  const whatsappUrl = `https://wa.me/${domainInfo.contact.whatsapp.replace(/\+/g, '')}?text=Hola, estoy interesado en comprar el dominio ${domainInfo.domain}`;

  return (
    <div className={styles.detailContainer}>
      <Helmet>
        <title>Venta de Dominio | {domainInfo.domain} | Rolsys Solutions</title>
        <meta name="description" content={`Compra el dominio premium ${domainInfo.domain}. ${domainInfo.description}`} />
      </Helmet>

      <motion.div
        className={styles.card}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Link to="/dominios" className={styles.backLink}>
          <span>← Volver</span>
        </Link>

        <span className={styles.badge}>Disponible</span>

        <h1 className={styles.domainName}>{domainInfo.domain}</h1>
        <p className={styles.description}>{domainInfo.description}</p>

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px', gap: '10px' }}>
          <button
            onClick={() => setCurrency('MXN')}
            style={{
              background: currency === 'MXN' ? '#3B82F6' : 'rgba(255,255,255,0.05)',
              color: '#fff', border: '1px solid rgba(255,255,255,0.1)', padding: '6px 16px', borderRadius: '20px', cursor: 'pointer', fontWeight: 600, transition: 'all 0.3s'
            }}
          >
            MXN
          </button>
          <button
            onClick={() => setCurrency('USD')}
            style={{
              background: currency === 'USD' ? '#3B82F6' : 'rgba(255,255,255,0.05)',
              color: '#fff', border: '1px solid rgba(255,255,255,0.1)', padding: '6px 16px', borderRadius: '20px', cursor: 'pointer', fontWeight: 600, transition: 'all 0.3s'
            }}
          >
            USD
          </button>
        </div>

        <div className={styles.priceContainer}>
          <span className={styles.priceLabel}>Precio actual estimado</span>
          <div className={styles.priceAmount}>{formatPrice(currentPrice)}</div>
        </div>

        <div className={styles.detailsGrid}>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Categoría</span>
            <span className={styles.detailValue}>{domainInfo.category}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Listado en</span>
            <span className={styles.detailValue}>
              {safeDate(domainInfo.listedDate).toLocaleDateString('es-MX')}
            </span>
          </div>
        </div>

        <div className={styles.comparisonModule}>
          <div className={`${styles.comparisonCol} ${styles.colLeft}`}>
            <h3 className={styles.comparisonTitle}>SI DECIDES LLEVARTELO</h3>
            <ul className={styles.comparisonList}>
              <li><SyncAltIcon className={styles.comparisonIcon} /> Gestión del código AuthCode para transferencia.</li>
              <li><SearchIcon className={styles.comparisonIcon} /> Alojamiento a cargo del comprador.</li>
              <li><HighlightOffIcon className={styles.comparisonIcon} /> Requiere diseño/desarrollo propio de web.</li>
              <li><WebAssetOffIcon className={styles.comparisonIcon} /> Sin servicio de correo ni soporte contunio.</li>
            </ul>
          </div>
          <div className={`${styles.comparisonCol} ${styles.colRight}`}>
            <h3 className={styles.comparisonTitle}>SI TE QUEDAS CON NOSOTROS:</h3>
            <ul className={styles.comparisonList}>
              <li><WebIcon className={styles.comparisonIcon} /> Dominio activo y configurado.</li>
              <li><WebAssetIcon className={styles.comparisonIcon} /> 1 año de Hosting incluido.</li>
              <li><CloudIcon className={styles.comparisonIcon} /> Landingpage lista para operar</li>
              <li><EmailIcon className={styles.comparisonIcon} /> Hasta 10 buzones de correo.</li>
              <li><SupportAgentIcon className={styles.comparisonIcon} /> Soporte técnico incluido.</li>
            </ul>
          </div>
        </div>

        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.actionButton}
        >
          Contactar por WhatsApp
        </a>

        {domainInfo.relatedLinks && domainInfo.relatedLinks.length > 0 && (
          <div className={styles.relatedLinks}>
            <h3 className={styles.relatedTitle}>Enlaces Patrocinados</h3>
            {domainInfo.relatedLinks.map((link, index) => (
              <div key={index} className={styles.adCard}>
                <h4>{link.title}</h4>
                <p>{link.description}</p>
                <a href={link.url} target="_blank" rel="noopener noreferrer" className={styles.adLink}>
                  Visitar Sitio ➔
                </a>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
